<div style="max-height: 80vh" class="max-w-screen-lg">
  <div class="w-screen max-w-5xl"></div>
  @if ((loading$ | async) === true) {
    <div class="spinner m-auto my-16 h-32 w-32 border-8"></div>
  }

  @if ((loading$ | async) === false) {
    <form [formGroup]="fg" (ngSubmit)="onSubmit()" class="ml-1">
      <div class="mb-2 text-xl font-semibold">
        {{ mode === 'add' ? 'Add New Vendor Contract' : 'Vendor Contract' }}
      </div>

      <div class="mb-3 grid grid-cols-5 gap-5">
        <aux-input
          class="mt-1"
          label="Entity Name"
          [formControl]="vendorFc"
          [showRequiredAsterisk]="true"
          [validators]="vendorNameValidators"
          auxTrimInput
        />
        @if (vendorCurrencyEnabled$ | async) {
          <div>
            <label for="currency" class="h-5 text-xs">
              Currency
              <span class="font-bold text-aux-error">*</span>
            </label>
            <ng-select
              id="currency"
              formControlName="currency"
              class="custom-outline select !mt-0"
              bindLabel="label"
              bindValue="key"
              auxFormError
              [fieldName]="'Currency'"
              [items]="currencyOptions"
              [clearable]="true"
              [searchable]="true"
              [appendTo]="'body'"
              [multiple]="false"
              [auxTooltip]="
                fg.get('currency')?.disabled
                  ? 'Currency cannot be changed after a budget is uploaded'
                  : ''
              "
            />
          </div>
        }
      </div>
      <div class="mb-3 grid grid-cols-5 gap-5">
        <div>
          <label for="vendor_type" class="h-5 text-xs">
            Vendor Contract Type
            <span class="font-bold text-aux-error">*</span>
          </label>
          <ng-select
            id="vendor_type"
            formControlName="vendor_type"
            bindLabel="label"
            bindValue="key"
            class="custom-outline select !mt-0"
            auxFormError
            [fieldName]="'Vendor Contract Type'"
            [items]="vendorTypeOptions"
            [searchable]="false"
            [clearable]="false"
            [appendTo]="'body'"
            [multiple]="false"
            [attr.data-pendo-id]="'pendo-vendor-contract-type'"
          />
        </div>
      </div>

      @if (getFc('is_third_party')?.value) {
        <div>
          <div class="aux-select">
            <label for="vendor">Select Parent Vendor Contract</label>
            <ng-select
              id="vendor"
              formControlName="parent_organization_id"
              bindValue="id"
              bindLabel="name"
              class="mb-3"
              [clearable]="false"
              [searchable]="false"
              [appendTo]="'body'"
            >
              @for (vendor of organizationQuery.selectAll() | async; track vendor) {
                <ng-option [value]="vendor">
                  <span [title]="vendor.name">{{ vendor.name }}</span>
                </ng-option>
              }
            </ng-select>
          </div>
          <aux-checkbox class="mb-3 text-sm" formControlName="costs_included_in_parent_wo">
            Costs included in parent WO?
          </aux-checkbox>
        </div>
      }

      <div class="mb-2 text-xl font-semibold">Primary Contact</div>
      <div class="mb-3 grid grid-cols-5 gap-5">
        <div>
          <aux-input
            id="given_name"
            formControlName="given_name"
            label="First Name"
            auxFormError
            (change)="refreshVendorValidators()"
          />
        </div>
        <div>
          <aux-input
            id="family_name"
            formControlName="family_name"
            label="Last Name"
            auxFormError
            (change)="refreshVendorValidators()"
          />
        </div>
        <div>
          <aux-input
            id="email"
            formControlName="email"
            label="Email"
            auxFormError
            (change)="refreshVendorValidators()"
          />
        </div>
        <div>
          <aux-input
            id="title"
            formControlName="title"
            label="Title"
            auxFormError
            (change)="refreshVendorValidators()"
          />
        </div>
        <div>
          <aux-input
            id="phone_number"
            formControlName="phone_number"
            label="Phone Number"
            auxFormError
            (change)="refreshVendorValidators()"
          />
        </div>
      </div>

      <aux-checkbox class="mb-3 text-sm" formControlName="costs_included_in_parent_wo">
        Costs included in parent WO?
      </aux-checkbox>

      <aux-checkbox
        class="mb-3 w-[230px] text-sm"
        formControlName="receives_vendor_estimate"
        [disabled]="isVendorEstimateExists"
        [auxTooltip]="
          isVendorEstimateExists
            ? 'Cannot be unchecked for Vendor Contract with estimates for current or future month.'
            : ''
        "
      >
        Provides monthly vendor estimates
      </aux-checkbox>
    </form>
  }

  <ng-container>
    <hr class="mb-4 ml-1 mt-8" />
    <div class="ml-1 mt-4 grid grid-cols-4 gap-5">
      <div>
        <div class="text-xl font-medium">Contracts</div>
        <div class="mb-2 h-10 text-sm">Upload Work Orders, MSA, RFP responses</div>
        <aux-file-manager
          #fileManager
          class="mb-4 h-24"
          [eager]="false"
          [pathFn]="getFilePaths('contracts')"
          [fetchFilesOnInit]="fetchFilesOnInit"
          [metadataFn]="getMetadata()"
          [document_entity_id]="vendorId"
          [document_entity_type_id]="ORGANIZATION"
          [document_type_id]="DOCUMENT_VENDOR_CONTRACT"
          (onFilesAdded)="onFilesAdded('contracts')"
          (filesRemoved)="onFilesRemoved()"
        />
        <aux-file-viewer
          [fileManager]="fileManager"
          [onlyShowUploaded]="false"
          [disableFirstFileMargin]="true"
        />
      </div>
      <div>
        <div class="text-xl font-medium">Proposal</div>
        <div class="mb-2 h-10 text-sm"></div>
        <aux-file-manager
          #fileManager2
          class="mb-4 h-24"
          [eager]="false"
          [pathFn]="getFilePaths('proposals')"
          [fetchFilesOnInit]="fetchFilesOnInit"
          [metadataFn]="getMetadata()"
          [document_entity_id]="vendorId"
          [document_entity_type_id]="ORGANIZATION"
          [document_type_id]="DOCUMENT_PROPOSAL"
          (onFilesAdded)="onFilesAdded('proposals')"
          (filesRemoved)="onFilesRemoved()"
        />
        <aux-file-viewer
          [fileManager]="fileManager2"
          [onlyShowUploaded]="false"
          [disableFirstFileMargin]="true"
        />
      </div>
      <div>
        <div class="text-xl font-medium">Other</div>
        <div class="mb-2 h-10 text-sm"></div>
        <aux-file-manager
          #fileManager3
          class="mb-4 h-24"
          [eager]="false"
          [pathFn]="getFilePaths('other')"
          [fetchFilesOnInit]="fetchFilesOnInit"
          [metadataFn]="getMetadata()"
          [document_entity_id]="vendorId"
          [document_entity_type_id]="ORGANIZATION"
          [document_type_id]="DOCUMENT_OTHER"
          (onFilesAdded)="onFilesAdded('other')"
          (filesRemoved)="onFilesRemoved()"
        />
        <aux-file-viewer
          [fileManager]="fileManager3"
          [onlyShowUploaded]="false"
          [disableFirstFileMargin]="true"
        />
      </div>
    </div>
  </ng-container>
</div>

<ng-template #description>
  <span class="mb-[5px]">
    All Vendor Contracts can be managed on the
    <span
      class="btn--hyperlink cursor-pointer p-0"
      (click)="router.navigateByUrl(vendorsRoutingPath); suggestionsModal?.close()"
    >
      Vendor Contracts
    </span>
    page
  </span>
</ng-template>
